import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useQuery, gql } from '@zenbusiness/application-commons-graphql';
import Title from '@zenbusiness/common-components/Title';
import { Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';

import Breadcrumbs from '#client/components/Breadcrumbs';
import { hasSkippedDomain } from '#client/utils/domains';
import { generateEntitlements } from '#client/utils/entitlements';

import DomainCard from './DomainCard';
import DomainPurchaseCard from './DomainPurchaseCard';
import DomainSkippedCard from './DomainSkippedCard';
import NoDomainsCard from './NoDomainsCard';

const messages = {
  header: 'Domains'
};

export const READ_DOMAINS = gql`
  query DomainList($businessEntityUuid: UUID!) {
    domains(businessEntityUuid: $businessEntityUuid) {
      domains {
        domainUuid
        domainName
        active
        configured
        domainPrivacy
        transferred
        expired
        verified
        srsExpireDatetime
        isExternal
        deleted
        websiteConnectionStatus
      }
    }
    orderTasksLegacy(businessEntityUuid: $businessEntityUuid) {
      orderTasksLegacy {
        orderTaskUuid
        orderTaskType
        orderTaskStatus
        orderTaskData {
          ...on DomainNameData {
            skip
          }
        }
      }
    }
  }
`;

const StyledBoxRoot = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2)
}));

const StyledTitlePage = styled(Title)(({ theme }) => ({
  ...theme.typography.title_extraLarge,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(6)
}));

const StyledBoxGridViewContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  width: '100%',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridGap: '1.5rem',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(22rem, 1fr))',
    paddingRight: '0.5rem'
  }
}));

const renderCards = (domains, entitlements, businessEntityUuid) => {
  const cards = domains
    .filter(({ deleted }) => !deleted)
    .map((domain) => (
      <Grid
        key={domain.domainUuid}
        container
        item
      >
        <DomainCard domain={domain} />
      </Grid>
    ));

  const activateSkippedDomain = (hasSkippedDomain(entitlements.get('domain_name_reg')) && (
    <Grid key="domain-skipped" item>
      <DomainSkippedCard businessEntityUuid={businessEntityUuid} />
    </Grid>
  ));

  const internalUpsell = (
    <Grid key="purchase-internal" container item>
      <DomainPurchaseCard businessEntityUuid={businessEntityUuid} />
    </Grid>
  );

  if (cards.length > 3) {
    cards.splice(2, 0, internalUpsell);
  } else {
    cards.push(activateSkippedDomain, internalUpsell);
  }

  return cards;
};

const DomainList = () => {
  const { businessEntityUuid } = useBusinessEntity();
  const {
    loading,
    data: {
      domains: {
        domains = []
      } = {},
      orderTasksLegacy: {
        orderTasksLegacy = []
      } = {}
    } = {}
  } = useQuery(READ_DOMAINS, {
    variables: {
      businessEntityUuid
    }
  });
  // TODO: Migrate to the new service to fetch entitlements, https://counsl.atlassian.net/browse/WEB-1807
  const entitlements = generateEntitlements(orderTasksLegacy);
  const overviewLink = `/d/company/${businessEntityUuid}/marketing-tools`;
  const handleOverviewClick = useLinkPressedCallback({
    elementName: 'overview_breadcrumb',
    elementText: 'Overview',
    linkDestination: overviewLink,
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domains_page'
  });

  if (!loading && domains.length <= 0 && !hasSkippedDomain(entitlements.get('domain_name_reg'))) {
    return (
      <NoDomainsCard
        businessEntityUuid={businessEntityUuid}
      />
    );
  }

  return (
    <StyledBoxRoot data-testid="domainList">
      <Breadcrumbs gutterBottom>
        <Button variant="text" color="primary" onClick={handleOverviewClick} component={RouterLink} to={overviewLink}>Overview</Button>
        <Button variant="text" color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains`}>Domains</Button>
      </Breadcrumbs>
      <StyledTitlePage level={1} variant="lg-alt">{messages.header}</StyledTitlePage>

      {!loading && (

      <StyledBoxGridViewContainer>
        {renderCards(domains, entitlements, businessEntityUuid)}
      </StyledBoxGridViewContainer>

      )}
    </StyledBoxRoot>
  );
};

export default DomainList;
